import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { postRentalFacilityAccountForm } from '../../../api/rentals';
import { AwaitButton } from '../../../reusable-components/await-button';
import CloseIconButton from '../../../reusable-components/basic-buttons/CloseIconButton';
import { useSnackbar } from '../../../reusable-components/snackbar';

RentalFacilityAccountForm.propTypes = {
  facilities: PropTypes.array,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
  open: PropTypes.bool,
  row: PropTypes.object,
  vendors: PropTypes.array,
};

export default function RentalFacilityAccountForm({ facilities, onClose, onSaved, open, row, vendors }) {
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState({});

  const facilityOptions = useMemo(
    () => (facilities || []).map((facility) => ({ value: facility.id, label: facility.shortName || facility.facilityName })),
    [facilities]
  );
  const vendorOptions = useMemo(
    () => (vendors || []).map((vendor) => ({ value: vendor.id, label: vendor.name })).sort((a, b) => a.label.localeCompare(b.label)),
    [vendors]
  );
  const selectedFacility = facilityOptions.find((facility) => facility.value === value.facilityId) ?? null;
  const selectedVendor = vendorOptions.find((vendor) => vendor.value === value.vendorId) ?? null;

  useEffect(() => {
    setValue({
      id: row?.facilityVendorId ?? 0,
      facilityId: row?.facilityId ?? 0,
      vendorId: row?.vendorId ?? 0,
      accountNumber: row?.accountNumber ?? '',
      isPreferred: row?.isPreferred ?? false,
    });
  }, [open, row]);

  const handleSave = async () => {
    try {
      const response = await postRentalFacilityAccountForm(value);
      if (!response?.success) {
        enqueueSnackbar(response?.message || 'Unable to save rental vendor account.', { variant: 'error' });
        return;
      }
      enqueueSnackbar('Rental vendor account saved successfully.', { variant: 'success' });
      onClose();
      await onSaved();
    } catch {
      enqueueSnackbar('Unable to save rental vendor account.', { variant: 'error' });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        {row ? 'Edit' : 'Add'} Rental Vendor Account
        <CloseIconButton onClick={onClose} />
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box>
              <Typography variant="subtitle1">Facility*</Typography>
              <Autocomplete
                fullWidth
                size="small"
                value={selectedFacility}
                options={facilityOptions}
                isOptionEqualToValue={(option, selected) => option.value === selected.value}
                onChange={(event, option) => setValue((current) => ({ ...current, facilityId: option?.value ?? 0 }))}
                renderInput={(params) => <TextField {...params} placeholder="Search or select" autoComplete="off" />}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Typography variant="subtitle1">Vendor*</Typography>
              <Autocomplete
                fullWidth
                size="small"
                value={selectedVendor}
                options={vendorOptions}
                isOptionEqualToValue={(option, selected) => option.value === selected.value}
                onChange={(event, option) => setValue((current) => ({ ...current, vendorId: option?.value ?? 0 }))}
                renderInput={(params) => <TextField {...params} placeholder="Search or select" autoComplete="off" />}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Typography variant="subtitle1">Account Number*</Typography>
              <TextField
                autoComplete="off"
                fullWidth
                size="small"
                value={value.accountNumber ?? ''}
                onChange={(event) => setValue((current) => ({ ...current, accountNumber: event.target.value }))}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(value.isPreferred)}
                  onChange={(event) => setValue((current) => ({ ...current, isPreferred: event.target.checked }))}
                />
              }
              label="Preferred vendor"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={onClose}>
          Cancel
        </Button>
        <AwaitButton
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!value.facilityId || !value.vendorId || !value.accountNumber?.trim()}
        >
          {row ? 'Save' : 'Add Account'}
        </AwaitButton>
      </DialogActions>
    </Dialog>
  );
}
